import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid2 as Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification } from "@refinedev/core";

import { Form } from "@shared/components";
import {
  AgreementsAutocomplete,
  AgreementProductsAutocomplete,
} from "@shared/components/AutocompleteFields";
import { TableCell } from "@shared/components/TableCells";
import { useTriggerSyncMutation } from "@modules/syncReceivedBooks/services/mutations";
import { useListSyncRunsQuery } from "@modules/syncReceivedBooks/services/queries";
import {
  syncReceivedBooksSchema,
  SyncReceivedBooksFormValues,
} from "@modules/syncReceivedBooks/validations";
import { SyncRunStatus } from "@modules/syncReceivedBooks/types";

const MONTH_OPTIONS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, index) => CURRENT_YEAR - 1 + index).map(
  (year) => ({ value: year, label: String(year) })
);

const STATUS_CONFIG: Record<
  SyncRunStatus,
  { label: string; color: "default" | "info" | "success" | "error" | "warning" }
> = {
  queued: { label: "Na fila", color: "default" },
  running: { label: "Rodando", color: "info" },
  completed: { label: "Concluído", color: "success" },
  failed: { label: "Falhou", color: "error" },
  rejected: { label: "Recusado", color: "warning" },
};

function StatusChip({ status }: { status: SyncRunStatus }) {
  const config = STATUS_CONFIG[status];

  return (
    <Chip
      size="small"
      color={config.color}
      label={config.label}
      icon={
        status === "completed" ? (
          <CheckCircleIcon />
        ) : status === "failed" || status === "rejected" ? (
          <ErrorIcon />
        ) : status === "running" || status === "queued" ? (
          <CircularProgress size={16} />
        ) : undefined
      }
    />
  );
}

export function SyncReceivedBooks() {
  const { open: openNotification } = useNotification();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const methods = useForm<SyncReceivedBooksFormValues>({
    resolver: zodResolver(syncReceivedBooksSchema()),
    defaultValues: {
      agreement_id: undefined,
      months: [],
      years: [],
      product_id: null,
      cpfs: "",
    },
  });

  const { mutate, isPending, error, reset: resetMutation } = useTriggerSyncMutation({
    onSuccess: () => {
      resetMutation();
      openNotification?.({
        type: "success",
        message: "Sincronização iniciada com sucesso.",
      });
      refetch();
    },
    onError: (mutationError) => {
      if (mutationError.response?.status !== 409) {
        openNotification?.({
          type: "error",
          message:
            mutationError.response?.data?.error ||
            "Erro ao iniciar a sincronização.",
        });
      }
    },
  });

  const { data, isLoading, refetch } = useListSyncRunsQuery({
    page: page + 1,
    limit,
  });

  const activeRun = error?.response?.data?.active_run;

  const selectedAgreementId = useWatch({ control: methods.control, name: "agreement_id" });

  useEffect(() => {
    methods.setValue("product_id", null);
  }, [selectedAgreementId, methods]);

  const handleSubmit = methods.handleSubmit((data) => {
    mutate(data);
  });

  return (
    <Grid container width={1} spacing={2}>
      <Paper sx={{ padding: "24px", width: "100%" }}>
        <Stack direction="column" spacing={3}>
          <Typography variant="h5" gutterBottom>
            Sincronizar Livros Recebidos
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Dispara manualmente a sincronização de livros recebidos de um
            convênio, em um mês/ano específicos. A execução é assíncrona —
            acompanhe o status no histórico abaixo.
          </Typography>

          <FormProvider {...methods}>
            <Stack direction="column" spacing={3}>
              {activeRun && (
                <Alert severity="warning">
                  Já existe uma execução em andamento com os mesmos
                  parâmetros (status: {STATUS_CONFIG[activeRun.status].label},
                  solicitada por {activeRun.requested_by_name}). Aguarde ela
                  terminar para disparar novamente.
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <AgreementsAutocomplete
                    name="agreement_id"
                    label="Convênio"
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Form.Select
                    name="months"
                    label="Meses"
                    options={MONTH_OPTIONS}
                    multiple
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Form.Select
                    name="years"
                    label="Anos"
                    options={YEAR_OPTIONS}
                    multiple
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <AgreementProductsAutocomplete
                    name="product_id"
                    label="Produto (opcional)"
                    agreement={selectedAgreementId}
                    disabled={!selectedAgreementId}
                    helperText={
                      !selectedAgreementId
                        ? "Selecione um convênio para escolher um produto."
                        : undefined
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Form.Input
                    name="cpfs"
                    label="CPFs (opcional, separados por vírgula)"
                  />
                </Grid>
              </Grid>

              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isPending}
                  size="large"
                >
                  {isPending ? "Sincronizando..." : "Sincronizar"}
                </Button>
              </Box>
            </Stack>
          </FormProvider>
        </Stack>
      </Paper>

      <Paper sx={{ padding: "24px", width: "100%" }}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h6">Histórico de Execuções</Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <MuiTableCell>ID</MuiTableCell>
                  <MuiTableCell>Status</MuiTableCell>
                  <MuiTableCell>Convênios</MuiTableCell>
                  <MuiTableCell>Período</MuiTableCell>
                  <MuiTableCell>Solicitado por</MuiTableCell>
                  <MuiTableCell>Criado em</MuiTableCell>
                  <MuiTableCell>Finalizado em</MuiTableCell>
                  <MuiTableCell>Erro</MuiTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <MuiTableCell colSpan={8} align="center">
                      <CircularProgress size={24} />
                    </MuiTableCell>
                  </TableRow>
                )}

                {!isLoading && data?.results.length === 0 && (
                  <TableRow>
                    <MuiTableCell colSpan={8} align="center">
                      Nenhuma execução encontrada.
                    </MuiTableCell>
                  </TableRow>
                )}

                {data?.results.map((run) => (
                  <TableRow key={run.id}>
                    <MuiTableCell>{run.id}</MuiTableCell>
                    <MuiTableCell>
                      <StatusChip status={run.status} />
                    </MuiTableCell>
                    <MuiTableCell>
                      {run.parameters.agreement_ids?.join(", ") || "-"}
                    </MuiTableCell>
                    <MuiTableCell>
                      {run.parameters.months?.join(", ")}/
                      {run.parameters.years?.join(", ")}
                    </MuiTableCell>
                    <MuiTableCell>{run.requested_by_name}</MuiTableCell>
                    <MuiTableCell>
                      <TableCell.Date value={run.created_at} />
                    </MuiTableCell>
                    <MuiTableCell>
                      <TableCell.Date value={run.finished_at} />
                    </MuiTableCell>
                    <MuiTableCell>{run.error_message || "-"}</MuiTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.count ?? 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Stack>
      </Paper>
    </Grid>
  );
}
